import { useCallback, useEffect, useMemo, useState } from 'react'
import { formatNumberWithCommas, isUserLoggedIn } from '@src/utils/functions'
import { formatBalance } from '@src/reducers/wallet.reducer'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import Env from '../RelayEnvironment'
import { fetchQuery, requestSubscription, useMutation, graphql } from 'react-relay'
import { useBJFMiningSubscription } from './__generated__/useBJFMiningSubscription.graphql'
import { useBJFMiningStateQuery } from './__generated__/useBJFMiningStateQuery.graphql'
import { useBJFMiningClaimMutation } from './__generated__/useBJFMiningClaimMutation.graphql'
import { refreshBJFMiningHistory } from './useBJFMiningHistory'

interface MiningProgress {
    progressId: number
    betTarget: number
    betProgress: number
    state: "ACTIVE" | "FINISHED" | "CLAIMABLE" | "CLAIMED" | "%future added value"
    finished: boolean // keeping for backward compatibility
    miningAmount: number
    payedOut: string | null
    payoutPrice: string | null
    insertedAt: string
    updatedAt: string
}

interface MiningState {
    unpaidBjf: number
    unpaidBjfUsd: number
    totalWager: number
    earnedBjf: number
    earnedBjfUsd: number
    progress: MiningProgress
    unlockDate: string
    claimableBjf: number
    claimableBjfUsd: number
}

interface ComputedProgress {
    isFinished: boolean
    progressPercentage: string
    wagerLeft: number
    currentWager: number
    targetWager: number
}

const useBJFMining = () => {
    const { t } = useTranslation()
    const isLoggedIn = isUserLoggedIn()
    const [miningState, setMiningState] = useState<MiningState | null>(null)
    const [isClaimLoading, setIsClaimLoading] = useState(false)
    const [claimBJF] = useMutation<useBJFMiningClaimMutation>(bjfMiningClaimMutation)

    const fetchInitialState = useCallback(() => {
        fetchQuery<useBJFMiningStateQuery>(Env, bjfMiningStateQuery, {})
            .toPromise()
            .then((response) => {
                if (response?.getBjfMiningState) {
                    setMiningState(response.getBjfMiningState)
                }
            })
            .catch((error) => {
                console.error('Error fetching BJF mining state:', error)
            })
    }, [])

    useEffect(() => {
        if(!isLoggedIn) return
        fetchInitialState()

        const subscriptionConfig = {
            subscription: bjfMiningUpdatedSubscription,
            variables: {},
            onNext: (response) => {
                if (response?.bjfMiningUpdated) {
                    setMiningState(currentState => ({
                        ...currentState!,
                        progress: response.bjfMiningUpdated
                    }))
                }

                // Refetch the mining state if the progress is finished
                if (response.bjfMiningUpdated.finished) {
                    fetchInitialState()
                }
            },
            onError: (error) => {
                console.error('Error updating BJF mining progress:', error)
            }
        }

        const subscriptionDisposable = requestSubscription<useBJFMiningSubscription>(Env, subscriptionConfig)

        return () => {
            subscriptionDisposable.dispose()
        }
    }, [fetchInitialState, isLoggedIn])

    const claimMinedBjf = useCallback(() => {
        if (!miningState?.claimableBjf || miningState.claimableBjf <= 0) {
            return Promise.reject(new Error('No claimable BJF available'))
        }

        setIsClaimLoading(true)
        
        return new Promise((resolve, reject) => {
            claimBJF({
                variables: {},
                onCompleted: (response) => {
                    if (response.claimMinedBjf?.minedAmount) {
                        const minedAmount = formatBalance({
                            balance: response.claimMinedBjf.minedAmount, 
                            currency: 'BJF'
                        }, false, false)
                        
                        // Show success toast
                        toast.success(t('toast.claimBJFSuccess', '{{minedAmount}} BJF claimed!', {minedAmount}))

                        // Refetch the mining state
                        fetchInitialState()
                        
                        // Refresh mining history to update table
                        refreshBJFMiningHistory()
                        
                        resolve(response.claimMinedBjf)
                    } else {
                        toast.error(t('toast.claimBJFError', 'Failed to claim BJF'))
                        reject(new Error('Failed to claim BJF'))
                    }
                    setIsClaimLoading(false)
                },
                onError: (error) => {
                    console.error('Error claiming BJF:', error)
                    setIsClaimLoading(false)
                    reject(error)
                }
            })
        })
    }, [miningState?.claimableBjf, claimBJF, fetchInitialState, t])

    const computeProgress = useCallback((): ComputedProgress => {
        if (!miningState?.progress) {
            return {
                isFinished: false,
                progressPercentage: '0',
                wagerLeft: 0,
                currentWager: 0,
                targetWager: 0
            }
        }

        const { betTarget, betProgress, finished } = miningState.progress
        const progressPercentage = Math.min(((betProgress / betTarget) * 100), 100)
        const wagerLeft = Math.max((betTarget - betProgress) / 100, 0)
        
        return {
            isFinished: finished,
            progressPercentage: progressPercentage.toFixed(0),
            wagerLeft: Number(wagerLeft.toFixed(0)),
            currentWager: Number((betProgress / 100).toFixed(0)),
            targetWager: Number((betTarget / 100).toFixed(0))
        }
    }, [miningState?.progress])

    const progress = useMemo(() => computeProgress(), [computeProgress])
    
    const isClaimable = useMemo(() => 
        (miningState?.claimableBjf || 0) > 0,
    [miningState?.claimableBjf])
    
    const claimableBjf = useMemo(() => 
        formatBalance({balance: miningState?.claimableBjf ?? 0, currency: 'BJF'}, false, false),
    [miningState?.claimableBjf])
       
    return {
        miningState,
        lockedBJF: formatBalance({balance: miningState?.unpaidBjf ?? 0, currency: 'BJF'}, false, false, 'BJF'),
        earnedBjf: formatBalance({balance: miningState?.earnedBjf ?? 0, currency: 'BJF'}, false, false, 'BJF'),
        earnedBjfUsd: miningState?.earnedBjfUsd ? miningState.earnedBjfUsd / 100 : 0,
        unpaidBjfUsd: miningState?.unpaidBjfUsd ? miningState.unpaidBjfUsd / 100 : 0,
        miningAmount: formatBalance({balance: miningState?.progress?.miningAmount ?? 0, currency: 'BJF'}, true, false, 'BJF'),
        totalWager: formatNumberWithCommas(miningState?.totalWager ? miningState.totalWager / 100 : 0),
        unlockDate: miningState?.unlockDate,
        isFinished: progress.isFinished,
        miningProgressPercentage: progress.progressPercentage,
        miningWagerLeft: progress.wagerLeft,
        currentWager: progress.currentWager,
        targetWager: progress.targetWager,
        isClaimable,
        claimableBjf,
        claimMinedBjf,
        isClaimLoading,
    }
}

export default useBJFMining

const bjfMiningStateQuery = graphql`
    query useBJFMiningStateQuery {
        getBjfMiningState {
            unpaidBjf
            unpaidBjfUsd
            unlockDate
            totalWager
            earnedBjf
            earnedBjfUsd
            claimableBjf
            claimableBjfUsd
            progress {
                progressId: id
                betTarget
                betProgress
                state
                finished
                miningAmount
                payedOut
                payoutPrice
                insertedAt
                updatedAt
            }
        }
    }
`
const bjfMiningUpdatedSubscription = graphql`
    subscription useBJFMiningSubscription {
        bjfMiningUpdated {
            progressId: id
            betTarget
            betProgress
            state
            finished
            miningAmount
            payedOut
            payoutPrice
            insertedAt
            updatedAt
        }
    }
`

const bjfMiningClaimMutation = graphql`
    mutation useBJFMiningClaimMutation {
        claimMinedBjf {
            minedAmount
        }
    }
`
